/**
 * Contenuti della pagina Novità (/news).
 *
 * Convenzione: ogni voce qui corrisponde a una entry `[public]` del CHANGELOG.md
 * (vedi sezione "Sistema Notifiche" e successive), riscritta in chiave cliente.
 * Aggiornare questo file quando si rilascia qualcosa di visibile all'utente.
 */

export type NewsCategory =
  | "Novità"
  | "Miglioramento"
  | "Correzione"
  | "Sicurezza";

export interface NewsItem {
  category: NewsCategory;
  title: string;
  description: string;
}

export interface NewsRelease {
  /** Etichetta data mostrata (es. "6 giugno 2026"). */
  date: string;
  /** Titolo opzionale del gruppo di novità. */
  headline?: string;
  items: NewsItem[];
}

/**
 * Annuncio mostrato UNA volta al primo accesso dopo un rilascio.
 *
 * `id` è la chiave con cui si ricorda che l'utente l'ha già visto: cambiarlo fa ricomparire il
 * popup a tutti, quindi si cambia solo quando c'è davvero qualcosa di nuovo da annunciare.
 * Tenere pochi punti: chi vuole il dettaglio ha la pagina Novità, raggiungibile dal popup stesso.
 */
export interface WhatsNew {
  id: string;
  title: string;
  intro: string;
  bullets: { title: string; text: string }[];
}

export const whatsNew: WhatsNew = {
  id: "2026-10-rimozioni-social",
  title: "Rimozioni seguite da noi e ricerca sui social",
  intro:
    "Le richieste di rimozione possiamo avviarle noi partendo dal tuo monitoraggio, puoi cercare il tuo nome anche sui social, e la pagina «Le mie rimozioni» ti dice con più precisione a che punto è ogni contenuto.",
  bullets: [
    {
      title: "Le rimozioni possiamo avviarle noi",
      text: "Il nostro team vede i risultati del tuo monitoraggio e può preparare e inviare per te le richieste di rimozione, anche se non entri nell'app. Le trovi nella tua area «Pratiche», senza usare i tuoi crediti per i report.",
    },
    {
      title: "Cerca anche sui social",
      text: "Nella nuova ricerca scegli la Modalità Social e le piattaforme: Facebook, Instagram, X, TikTok, YouTube e Reddit. Ogni piattaforma vale 1 credito e il totale lo vedi prima di avviare la ricerca.",
    },
    {
      title: "Non più nei motori di ricerca",
      text: "Se un sito lascia online la pagina ma la esclude da Google e dagli altri motori, ora lo riconosciamo e lo vedi con uno stato dedicato, separato dalle rimozioni.",
    },
    {
      title: "Quello che dice il sito, e quello che verifichiamo",
      text: "Quando un sito ci risponde di aver tolto un contenuto lo vedi come «Dichiarato dal sito», contato a parte fra quelli in verifica. Lo contiamo fra le rimozioni solo dopo averlo controllato noi.",
    },
  ],
};

export const newsReleases: NewsRelease[] = [
  {
    date: "5 ottobre 2026",
    headline: "Le richieste di rimozione possiamo avviarle noi",
    items: [
      {
        category: "Novità",
        title: "Rimozioni avviate dal nostro team",
        description:
          "Il nostro team vede i risultati del monitoraggio automatico e può preparare e inviare per te le richieste di rimozione, anche se non entri nell'app. Le pratiche avviate da noi compaiono nella tua area «Pratiche» come quelle che crei tu, non usano i tuoi crediti per i report e rientrano, come sempre, nel limite mensile di rimozioni del tuo piano.",
      },
    ],
  },
  {
    date: "18 settembre 2026",
    headline: "Le risposte dei siti, in chiaro",
    items: [
      {
        category: "Miglioramento",
        title: "Prima vedi la risposta, poi la verifichiamo",
        description:
          "Quando un sito ci risponde di aver tolto un contenuto e il nostro team registra la risposta, in «Le mie rimozioni» lo vedi come «Dichiarato dal sito», con un contatore a parte «in verifica». Lo contiamo fra le rimozioni confermate solo quando il nostro controllo automatico verifica che il contenuto non c'è più: se invece è ancora online, torna fra i contenuti da rimuovere.",
      },
    ],
  },
  {
    date: "17 settembre 2026",
    headline: "Rimozioni: vedi anche cosa sparisce dai motori di ricerca",
    items: [
      {
        category: "Novità",
        title: "Nuovo stato «Non più nei motori»",
        description:
          "Alcuni siti non cancellano la pagina ma chiedono a Google e agli altri motori di non mostrarla più. Ora lo riconosciamo: in «Le mie rimozioni» la vedi con uno stato dedicato e un contatore a parte, e ti avvisiamo con una notifica nell'app. Non la contiamo fra le rimozioni, perché la pagina esiste ancora: continuiamo a controllarla e, se il sito torna a renderla visibile, la riportiamo fra i link ancora online.",
      },
      {
        category: "Miglioramento",
        title: "Rimozioni riconosciute anche dietro un rimando",
        description:
          "Quando un sito toglie un articolo, a volte il vecchio link passa prima per un rimando automatico (per esempio da http a https, o dall'indirizzo con www a quello senza) e solo dopo arriva alla pagina «non trovata». Prima ci fermavamo al rimando e il contenuto restava tra quelli online. Ora seguiamo il percorso fino in fondo: se finisce su una pagina che non esiste più, dopo i soliti due controlli lo contiamo tra i rimossi.",
      },
    ],
  },
  {
    date: "28 agosto 2026",
    headline: "Cerca il tuo nome anche sui social",
    items: [
      {
        category: "Novità",
        title: "Modalità Social",
        description:
          "Nella nuova ricerca puoi scegliere la Modalità Social e le piattaforme su cui cercare, una o più tra Facebook, Instagram, X, TikTok, YouTube e Reddit. Troviamo i post pubblici che compaiono sui motori di ricerca (su Reddit, se attivi l'opzione, anche i commenti) e i risultati compaiono man mano che li troviamo, senza aspettare la fine della ricerca.",
      },
      {
        category: "Miglioramento",
        title: "Il costo della ricerca, prima di avviarla",
        description:
          "Prima di avviare una ricerca vedi quanti crediti userà. Una ricerca standard vale 1 credito; in Modalità Social ogni piattaforma è una ricerca a sé e vale 1 credito, e il totale si aggiorna mentre le selezioni. Il monitoraggio automatico incluso nel tuo piano non consuma questi crediti.",
      },
      {
        category: "Miglioramento",
        title: "Nessun risultato, crediti restituiti",
        description:
          "Se una ricerca si chiude senza nessun risultato, al termine ti restituiamo automaticamente tutti i crediti che ha usato. Vale anche per la Modalità Social, dove la ricerca costa un credito per ogni piattaforma selezionata: ora li recuperi tutti, non uno solo.",
      },
      {
        category: "Correzione",
        title: "Se una ricerca non parte, te lo diciamo",
        description:
          "Se la ricerca viene rifiutata al momento del lancio, per esempio perché i crediti non bastano per le piattaforme scelte, resti sul modulo e in cima alla pagina compare un avviso di errore, invece di una schermata di attesa ferma a zero.",
      },
    ],
  },
  {
    date: "28 luglio 2026",
    headline: "Come lavoriamo alle tue rimozioni",
    items: [
      {
        category: "Miglioramento",
        title: "Ogni rimozione è verificata due volte",
        description:
          "Un contenuto risulta rimosso solo dopo due controlli indipendenti a distanza di ore: un sito momentaneamente irraggiungibile non viene più scambiato per una rimozione riuscita.",
      },
      {
        category: "Novità",
        title: "Sorveglianza sui contenuti già rimossi",
        description:
          "Continuiamo a ricontrollare anche ciò che è già stato tolto. Se qualcuno prova a ripubblicarlo, il contenuto torna in lavorazione da solo e la richiesta riparte.",
      },
      {
        category: "Novità",
        title: "Pagine di ricerca trattate a parte",
        description:
          "Alcuni indirizzi non sono contenuti: sono pagine che il sito genera al momento in cui qualcuno cerca un nome, e nessun gestore può cancellarle. Ora vengono indirizzate ai motori di ricerca invece che al sito, e non pesano più sul conteggio delle rimozioni.",
      },
      {
        category: "Novità",
        title: "Nessun caso resta indietro in silenzio",
        description:
          "Se un sito non risponde, dopo 14 giorni il caso ci viene segnalato automaticamente e sollecitiamo il sito; a 30 giorni di nuovo. Se dopo 60 giorni il contenuto è ancora online, il caso viene segnalato per la valutazione legale con la storia completa dei tentativi.",
      },
      {
        category: "Miglioramento",
        title: "Rimozione dai motori di ricerca tracciata",
        description:
          "Le richieste inviate a Google e Bing sono ora registrate con data ed esito, così nel report vedi separatamente cosa è stato tolto dai siti e cosa è sparito dai risultati di ricerca.",
      },
      {
        category: "Miglioramento",
        title: "Percentuali di rimozione più accurate",
        description:
          "La percentuale è ora calcolata solo sui contenuti effettivamente rimovibili. Prima il denominatore comprendeva anche le pagine che nessun gestore può cancellare, e questo la faceva risultare più bassa del lavoro realmente svolto.",
      },
    ],
  },
  {
    date: "9 giugno 2026",
    headline: "Ricerche più precise e in italiano",
    items: [
      {
        category: "Miglioramento",
        title: "Ricerche più complete",
        description:
          "Ogni ricerca ora considera automaticamente le varianti del nome (plurali e femminili), così non ti sfuggono risultati rilevanti.",
      },
      {
        category: "Miglioramento",
        title: "Sentiment più chiaro, in italiano",
        description:
          "La valutazione di ogni risultato ora è descritta in italiano e basata sull'impatto reale sulla tua reputazione: un contenuto dannoso resta «Negativo» anche se scritto con toni gentili.",
      },
    ],
  },
  {
    date: "6 giugno 2026",
    headline: "Mia Reputation ora ti avvisa",
    items: [
      {
        category: "Novità",
        title: "Notifiche in tempo reale",
        description:
          "Un nuovo campanellino 🔔 in alto ti segnala subito quando un report è pronto, una ricerca è completata o cambia lo stato di una richiesta di rimozione.",
      },
      {
        category: "Novità",
        title: "Avvisi via email",
        description:
          "Ricevi un'email quando la tua ricerca è completata o il tuo report è pronto, così resti aggiornato anche quando non sei nell'app.",
      },
      {
        category: "Novità",
        title: "Riepilogo rimozioni",
        description:
          "Gli aggiornamenti sulle richieste di rimozione arrivano raccolti in un'unica email riepilogativa periodica, senza intasare la casella con un messaggio per ogni cambiamento.",
      },
      {
        category: "Miglioramento",
        title: "Analisi più veloce",
        description:
          "L'analisi del sentiment dei risultati di ricerca è ora molto più rapida: i dati compaiono progressivamente mentre la ricerca è ancora in lavorazione.",
      },
    ],
  },
  {
    date: "1 giugno 2026",
    items: [
      {
        category: "Novità",
        title: "Contatore rimozioni",
        description:
          "In dashboard vedi a colpo d'occhio quante rimozioni hai utilizzato rispetto al tuo piano.",
      },
      {
        category: "Miglioramento",
        title: "Nuova veste grafica",
        description:
          "Pagine di accesso e landing ridisegnate con il brand Mia Reputation, più chiare e coerenti.",
      },
      {
        category: "Correzione",
        title: "Layout più stabile",
        description:
          "Risolti alcuni problemi di visualizzazione (footer sempre visibile e scroll) su diverse pagine.",
      },
    ],
  },
  {
    date: "23 maggio 2026",
    items: [
      {
        category: "Novità",
        title: "Nuovo indirizzo dell'app",
        description:
          "L'app è ora raggiungibile su app.miareputation.com, più coerente con il marchio.",
      },
      {
        category: "Miglioramento",
        title: "Da WebReputation a Mia Reputation",
        description:
          "Completato il rebranding in tutta l'applicazione: nuovo nome, nuovo look.",
      },
    ],
  },
];

/** Anteprime di ciò che sta per arrivare (entry `[public]` ancora in [Unreleased]). */
export const upcomingNews: NewsItem[] = [
  {
    category: "Novità",
    title: "EMERGENCY Crisis Pack",
    description:
      "Un pacchetto one-shot dedicato alle crisi reputazionali acute, per intervenire in modo immediato.",
  },
  {
    category: "Novità",
    title: "Bonus WELCOME30",
    description:
      "Sconto del 30% sul primo anno dei piani con fatturazione annuale, riservato a chi richiede il report gratuito su miareputation.com: il link con lo sconto arriva via email.",
  },
];
